import { convertToDollars } from '@shared/utils';
import { Deduction, Earnings } from 'app/core/models/net-income.interface';

//* US States
const states = [
  ['Alabama', 'AL'],
  ['Alaska', 'AK'],
  ['American Samoa', 'AS'],
  ['Arizona', 'AZ'],
  ['Arkansas', 'AR'],
  ['Armed Forces Americas', 'AA'],
  ['Armed Forces Europe', 'AE'],
  ['Armed Forces Pacific', 'AP'],
  ['California', 'CA'],
  ['Colorado', 'CO'],
  ['Connecticut', 'CT'],
  ['Delaware', 'DE'],
  ['District Of Columbia', 'DC'],
  ['Florida', 'FL'],
  ['Georgia', 'GA'],
  ['Guam', 'GU'],
  ['Hawaii', 'HI'],
  ['Idaho', 'ID'],
  ['Illinois', 'IL'],
  ['Indiana', 'IN'],
  ['Iowa', 'IA'],
  ['Kansas', 'KS'],
  ['Kentucky', 'KY'],
  ['Louisiana', 'LA'],
  ['Maine', 'ME'],
  ['Marshall Islands', 'MH'],
  ['Maryland', 'MD'],
  ['Massachusetts', 'MA'],
  ['Michigan', 'MI'],
  ['Minnesota', 'MN'],
  ['Mississippi', 'MS'],
  ['Missouri', 'MO'],
  ['Montana', 'MT'],
  ['Nebraska', 'NE'],
  ['Nevada', 'NV'],
  ['New Hampshire', 'NH'],
  ['New Jersey', 'NJ'],
  ['New Mexico', 'NM'],
  ['New York', 'NY'],
  ['North Carolina', 'NC'],
  ['North Dakota', 'ND'],
  ['Northern Mariana Islands', 'NP'],
  ['Ohio', 'OH'],
  ['Oklahoma', 'OK'],
  ['Oregon', 'OR'],
  ['Pennsylvania', 'PA'],
  ['Puerto Rico', 'PR'],
  ['Rhode Island', 'RI'],
  ['South Carolina', 'SC'],
  ['South Dakota', 'SD'],
  ['Tennessee', 'TN'],
  ['Texas', 'TX'],
  ['US Virgin Islands', 'VI'],
  ['Utah', 'UT'],
  ['Vermont', 'VT'],
  ['Virginia', 'VA'],
  ['Washington', 'WA'],
  ['West Virginia', 'WV'],
  ['Wisconsin', 'WI'],
  ['Wyoming', 'WY'],
];

//* Canada Provinces
const provinces = [
  ['Alberta', 'AB'],
  ['British Columbia', 'BC'],
  ['Manitoba', 'MB'],
  ['New Brunswick', 'NB'],
  ['Newfoundland', 'NF'],
  ['Northwest Territory', 'NT'],
  ['Nova Scotia', 'NS'],
  ['Nunavut', 'NU'],
  ['Ontario', 'ON'],
  ['Prince Edward Island', 'PE'],
  ['Quebec', 'QC'],
  ['Saskatchewan', 'SK'],
  ['Yukon', 'YT'],
];

/** Bank Account Types that come back from the Server */
const accountTypes: Record<string, string> = {
  C: 'Checking',
  S: 'Savings',
  P: 'Paper Check',
};

/*** Capitalize first letter in string. */
export const capitalizeFirstLetter = (name: string) => name[0].toUpperCase() + name.slice(1);

/** Clean string to lowercase and trim so that we can use text-transform: Capitalize in css. */
export const cleanString = (str: string | string[]) =>
  typeof str === 'string' ? str.toLowerCase().trim() : str;

/*** Return string $ Amount per paycheck.
 ** ***ex. => $200 per paycheck***
 */
export const dollarPerPaycheck = (amount: number) => `${convertToDollars(amount)} per paycheck`;

/*** Return 'Checking', 'Savings', or 'Paper Check' Account Type depending on Letter ('C','S','P') coming back from server. */
export const formatAccountType = (account: string) => accountTypes[account];

/*** Format Address string from an Array of Address strings. */
export const formatAddress = (addressInfo: Array<string>) =>
  [...addressInfo].map(data => capitalizeFirstLetter(data.toLowerCase())).join(' ');

/*** Format Date from tick value. */
export const formatDateLocalTime = (date: string) =>
  new Date(date)
    .toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/(\d+)\/(\d+)\/(\d+)/, '$1/$2/$3');

/***
 * Format Date from date string.
 * Date string should be in format: '1996-02-21'
 * */
export const formatDate = (date: string) => {
  if (!date) return '';
  const [year, month, day] = date.split('-').map(n => n.padStart(2, '0'));
  return `${month}/${day}/${year}`;
};

/*** Format Names string from an Array of Name strings */
export const formatNames = (names: string[]) =>
  names.map(name => capitalizeFirstLetter(name.toLowerCase())).join(' ');

/*** Format string type to phone number format:
 * ***Ex: (123) 456-7890***
 *  */
export const formatPhoneNumber = (phoneNumberString: string) => {
  const cleaned = ('' + phoneNumberString)?.replace(/\D/g, '');
  const match = cleaned?.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
};

export const formatSplitAmount = (amount: number, depositType: string, isRemainder: boolean) => {
  const isDollar = depositType === 'Dollar';
  const remainderValue = isDollar ? 'Remainder' : `${amount}%`;

  const dollarValue = amount ? convertToDollars(`${amount}`) : null;
  const percentValue = amount ? `${amount}%` : null;

  return isRemainder ? remainderValue : isDollar ? dollarValue : percentValue;
};

/* show N/A instead of accountName when all the following applies
  1.accountName is empty
  2.the bank record is remainder
  3. account type is Paper check
  probably just accountName is empty is enough but needs more investigation
*/
export const accountNameOrNA = (accountName: string, isRemainder: boolean, accountType: string) =>
  `${isRemainder && accountType === 'P' && accountName === '' ? 'N/A' : accountName}`;

/*** Return Male or Female string. */
export const maleOrFemale = (sex: string) => (sex === 'M' ? 'Male' : 'Female');

/*** Return % Amount string per paycheck.*/
export const percentPerPaycheck = (amount: number) => (amount ? `${amount}% per paycheck` : null);

/*** Return 'Tax-exempt' or 'Taxable' string, depending on boolean value. */
export const taxExemptOrTaxable = (bool: boolean) => (bool ? 'Tax-exempt' : 'Taxable');

/*** Convert US state abbreviation to full name and vice-versa. ex.
 ** ***'IL => 'Illinois'***
 */
export const UsStateAbbrvToName = (input?: string, isnonTaxState?: boolean): string | null => {
  const toAbbr = input?.length !== 2;

  const regions = states?.concat(provinces);

  let i; // Reusable loop variable

  if (toAbbr) {
    input = input?.replace(/\w\S*/g, function (txt: string) {
      return txt?.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
    for (i = 0; i < regions?.length; i++) {
      if (regions[i][0] === input) {
        return regions[i][1];
      }
    }
  } else {
    input = input?.toUpperCase();
    for (i = 0; i < regions.length; i++) {
      if (regions[i][1] === input) {
        return regions[i][0];
      }
    }
  }

  return null;
};

export const stateAndNoIncomeTax = (input?: string, isNonTaxState?: boolean): string | null => {
  const newStateString = UsStateAbbrvToName(input);
  return isNonTaxState ? `${newStateString} - no state income taxes` : newStateString;
};

/*** Return Yes or No string from True or False boolean. */
export const yesOrNoFromBoolean = (bool: boolean) => (bool ? 'Yes' : 'No');

/*** Federal Taxes Card has 2 versions (2019 | 2020+) that depends on which W4 version, decided by the year. */
export const is2019w4 = (year: string) => (year === '2019' ? '2019' : '2020+');
